import styled from "@emotion/styled";
import { Button, FlexRow, List, Stack, Tabs } from "atoms";
import { OpenCallSubmissionCard } from "./OpenCallSubmissionCard";
import { useOpenCallSubmissions } from "../api/useOpenCallSubmissions";
import { OpenCall, OpenCallTag } from "../types";

function OpenCallTabs({
  openCall,
  tag,
}: {
  openCall: OpenCall;
  tag: OpenCallTag;
}) {
  if (tag === "Submissions Open" && !openCall.submissionEndDate) return null;

  const submissionsTabs = [
    {
      title: "Selected Artwork",
      content: (
        <OpenCallSubmissionTabs
          openCallId={openCall.id}
          category="selected"
          displayCount={openCall.maxWinners}
        />
      ),
      isShowing: openCall.isSelectionCompleted,
    },
    {
      title: "Community Favorites",
      content: (
        <OpenCallSubmissionTabs
          openCallId={openCall.id}
          category="favorites"
          displayCount={openCall.maxCommunityFavorites}
        />
      ),
      isShowing: openCall.isSelectionCompleted && openCall.isCommunityCuration,
    },
    {
      title: "All Submissions",
      content: (
        <OpenCallSubmissionTabs openCallId={openCall.id} category="all" />
      ),
      isShowing: openCall.showSubmissions,
    },
  ];

  return (
    <div id="submissions">
      <Tabs tabs={submissionsTabs} />
    </div>
  );
}

type OpenCallSubmissionTabsProps = {
  openCallId: string;
  /**
   * Subset of submissions to be displayed, defaults to "all"
   */
  category?: "all" | "selected" | "favorites";
  /**
   * Number of submissions to be displayed, defaults to 24
   */
  displayCount?: number;
};

function OpenCallSubmissionTabs({
  openCallId,
  category = "all",
  displayCount = 24,
}: OpenCallSubmissionTabsProps) {
  const { data, hasNextPage, fetchNextPage } = useOpenCallSubmissions({
    openCallId,
    selectedOnly: category === "selected",
    perPage: displayCount,
    sort:
      category === "favorites"
        ? ["communityVoteCount desc", "title"]
        : ["title"],
  });

  let submissions = data?.pages?.flatMap((page) => page.data) ?? [];

  if (submissions.length === 0) return null;

  // If favorites were requested, re-sort by title instead of vote count
  if (category === "favorites") {
    submissions = submissions.sort((a, b) =>
      a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1
    );
  }

  const loadMoreSubmissions = () => fetchNextPage();

  return (
    <Stack gap="sm">
      <SubmissionGrid>
        {submissions.map((submission) => (
          <OpenCallSubmissionCard
            key={submission.submissionId}
            submission={submission}
            showArtist
          />
        ))}
      </SubmissionGrid>
      {hasNextPage && (
        <FlexRow justifyContent="center">
          <Button size="sm" onClick={loadMoreSubmissions}>
            Load more...
          </Button>
        </FlexRow>
      )}
    </Stack>
  );
}

const SubmissionGrid = styled(List)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing.gap,
  gridTemplateColumns: "repeat(auto-fill, minmax( calc(120px + 5vw), 1fr))",
  [theme.breakpoints.desktop]: {
    gridTemplateColumns: "repeat(4, minmax(120px, 1fr))",
  },
}));
SubmissionGrid.defaultProps = {
  type: "blank",
};

export { OpenCallTabs };
